import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon, MenuIcon, XIcon } from "lucide-react";
import { Logo } from "./Logo";
import { MobileMenu } from "./MobileMenu";
import { navItems } from "../data/navigation";
const ease = [0.23, 1, 0.32, 1] as const;
export function Header() {
  const {
    pathname
  } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [pathname]);
  const solid = scrolled || pathname !== "/" || mobileOpen;
  return <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-200 ${solid ? "border-b border-line bg-paper/95 backdrop-blur" : "border-b border-transparent bg-transparent"}`} data-id="element-100" data-mp="2wl4eqo7g82f5">
      <div className="mx-auto flex h-20 max-w-site items-center justify-between gap-6 px-5 md:px-8" data-id="element-101" data-mp="2morawxi6w86o">
        <Logo tone={solid ? "dark" : "light"} data-id="element-102" data-mp="2hpoiqdnb5wg4" />

        <nav aria-label="Navigation principale" className="hidden xl:block" data-id="element-103" data-mp="3fbluztb3h5jm">
          <ul className="flex items-center gap-1" data-id="element-104" data-mp="3acizp0kx2dou">
            {navItems.map(item => {
            const isOpen = openMenu === item.label;
            const linkTone = solid ? "text-ink hover:bg-sand" : "text-white hover:bg-white/10";
            if (!item.children) {
              return <li key={item.label} data-id="element-105" data-mp="1wzaccnd9ff2">
                    <Link to={item.href} className={`whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors duration-150 ${linkTone}`} data-id="element-106" data-mp="1y0jucoqhtuo">
                      {item.label}
                    </Link>
                  </li>;
            }
            return <li key={item.label} className="relative" onMouseEnter={() => setOpenMenu(item.label)} onMouseLeave={() => setOpenMenu(null)} data-id="element-107" data-mp="1hojncpsm2yl0">
                  <button type="button" aria-expanded={isOpen} aria-haspopup="true" onClick={() => setOpenMenu(isOpen ? null : item.label)} className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors duration-150 ${linkTone}`} data-id="element-108" data-mp="18lij6unb3hjz">
                    {item.label}
                    <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" data-id="element-109" data-mp="2y20127urxdof" />
                  </button>
                  <AnimatePresence data-id="element-110" data-mp="hgcxmyzgfd5u">
                    {isOpen && <motion.div initial={{
                  opacity: 0,
                  y: 6,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 6,
                  scale: 0.98
                }} transition={{
                  duration: 0.18,
                  ease
                }} className="absolute left-0 top-full w-80 origin-top-left pt-3" data-id="element-111" data-mp="w47p6c1q7bhc">
                        <ul className="rounded-2xl border border-line bg-paper p-2 shadow-xl shadow-ink/10" data-id="element-112" data-mp="1f324bm53qdy1">
                          {item.children.map(child => <li key={child.href} data-id="element-113" data-mp="1a2653lvp3fyy">
                              <Link to={child.href} className="block rounded-xl px-4 py-3 transition-colors duration-150 hover:bg-sand focus:bg-sand focus:outline-none" data-id="element-114" data-mp="3gvqbg6kv9lj3">
                                <span className="block text-sm font-semibold text-ink" data-id="element-115" data-mp="27xrukcoiut4m">{child.label}</span>
                                <span className="mt-0.5 block text-sm text-muted" data-id="element-116" data-mp="2cx4il0r4q68s">{child.description}</span>
                              </Link>
                            </li>)}
                        </ul>
                      </motion.div>}
                  </AnimatePresence>
                </li>;
          })}
          </ul>
        </nav>

        <div className="flex items-center gap-3" data-id="element-117" data-mp="1t9424f9z2ziw">
          <Link to="/contact" className="hidden whitespace-nowrap rounded-full bg-clay px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-clay-dark sm:inline-flex" data-id="element-118" data-mp="1t7ay77qqutel">
            Demander un devis
          </Link>
          <button type="button" onClick={() => setMobileOpen(v => !v)} aria-expanded={mobileOpen} aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"} className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors duration-150 xl:hidden ${solid ? "text-ink hover:bg-sand" : "text-white hover:bg-white/10"}`} data-id="element-119" data-mp="15i86t86b5m6w">
            {mobileOpen ? <XIcon className="h-6 w-6" data-id="element-120" data-mp="f0wbxa4gq8os" /> : <MenuIcon className="h-6 w-6" data-id="element-121" data-mp="f166lb0gd40t" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-122" data-mp="a9o4ukqluzes">{mobileOpen && <MobileMenu data-id="element-123" data-mp="a8mpw56gxsrs" />}</AnimatePresence>
    </header>;
}